import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import axios from 'axios';
import { FaStar } from 'react-icons/fa';
import { TailSpin } from 'react-loader-spinner';
import { StoreContext } from '../context/StoreContext';

function RateRestaurant() {
  const { positionGeohash, restaurantId } = useParams();
  const location = useLocation();
  const { userId, accessToken, setRequestId, requestId } = useContext(StoreContext);

  const [sessionId, setSessionId] = useState(null);
  const [sessionChecked, setSessionChecked] = useState(false);
  const [restaurantDetails, setRestaurantDetails] = useState(null);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(null);
  const [feedback, setFeedback] = useState('');
  const [loadingRestaurant, setLoadingRestaurant] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [ratingSubmitted, setRatingSubmitted] = useState(false);
  const [submittingRating, setSubmittingRating] = useState(false);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const sessionFromQuery = queryParams.get('sessionId');
    setSessionId(sessionFromQuery);
    setSessionChecked(true);

    if (sessionFromQuery && sessionFromQuery !== requestId) {
      setRequestId(sessionFromQuery);
    }
  }, [location.search, requestId, setRequestId]);

  useEffect(() => {
    const fetchRestaurantDetails = async () => {
      try {
        setLoadingRestaurant(true);
        setError('');
        const response = await axios.get(
          `/api/catalogue/restaurant/${positionGeohash}/${restaurantId}/summary`,
          {
            headers: {
              accept: 'application/json',
            },
          }
        );
        setRestaurantDetails(response.data);
      } catch (err) {
        console.error('Error fetching restaurant details:', err);
        setError('We could not load the restaurant details. Please refresh the page.');
      } finally {
        setLoadingRestaurant(false);
      }
    };

    if (positionGeohash && restaurantId) {
      fetchRestaurantDetails();
    }
  }, [positionGeohash, restaurantId]);

  const isAuthReady = useMemo(() => Boolean(userId && accessToken), [userId, accessToken]);
  const waitingForSession = sessionChecked && sessionId && !isAuthReady;
  const showLoader = loadingRestaurant || !sessionChecked || waitingForSession;

  const handleAutoSubmit = useCallback(async () => {
    setSubmittingRating(true);
    setError('');

    try {
      const payload = {
        positionId: positionGeohash,
        restaurantId,
        userId,
        rating: 5,
        feedback: '5-star rating - excellent experience',
      };

      const response = await axios.post('/api/nexus/feedback/restaurant', payload, {
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
          'auth-token': accessToken,
        },
      });

      if (response.data?.success === false) {
        setError(response.data?.message || 'Failed to submit feedback. Please try again.');
        return;
      }

      setRatingSubmitted(true);
    } catch (submitError) {
      console.error('Error submitting restaurant feedback:', submitError);
      setError('Failed to submit your feedback. Please try again.');
    } finally {
      setSubmittingRating(false);
    }
  }, [accessToken, positionGeohash, restaurantId, userId]);

  useEffect(() => {
    if (
      rating === 5 &&
      sessionId &&
      restaurantDetails &&
      isAuthReady &&
      !ratingSubmitted &&
      !submittingRating
    ) {
      handleAutoSubmit();
    }
  }, [
    handleAutoSubmit,
    isAuthReady,
    rating,
    ratingSubmitted,
    restaurantDetails,
    sessionId,
    submittingRating
  ]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!sessionId) {
      setError('Session ID missing. Please use the rating link shared with you.');
      return;
    }

    if (!isAuthReady) {
      setError('We are still validating your session. Please wait a moment.');
      return;
    }

    if (rating === 0) {
      setError('Please select a rating before submitting.');
      return;
    }

    if (rating === 5 && ratingSubmitted) {
      return; // Already auto-submitted
    }

    setSubmitting(true);
    setError('');

    try {
      const payload = {
        positionId: positionGeohash,
        restaurantId,
        userId,
        rating,
        feedback: feedback.trim(),
      };

      const response = await axios.post('/api/nexus/feedback/restaurant', payload, {
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
          'auth-token': accessToken,
        },
      });

      if (response.data?.success === false) {
        setError(response.data?.message || 'Failed to submit feedback. Please try again.');
        return;
      }

      setSubmitted(true);
    } catch (submitError) {
      console.error('Error submitting restaurant feedback:', submitError);
      setError('Failed to submit your feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleMapsRating = () => {
    if (restaurantDetails?.socialMedia?.GOOGLE_MAPS) {
      window.open(restaurantDetails.socialMedia.GOOGLE_MAPS, '_blank');
    }
    setSubmitted(true);
    setTimeout(() => {
      window.location.href = 'https://tavahot.com';
    }, 3000);
  };

  const skipGoogleMapsRating = () => {
    setSubmitted(true);
    setTimeout(() => {
      window.location.href = 'https://tavahot.com';
    }, 3000);
  };

  if (showLoader) {
    return (
      <div className="pt-24 flex flex-col w-full h-full items-center bg-[#FBFCFF]">
        <TailSpin
          height="80"
          width="80"
          color="#FBC02D"
          ariaLabel="loading"
          radius="1"
          visible={true}
        />
        <p className="mt-4 text-sm text-[#696974]">
          {waitingForSession ? 'Validating your session…' : 'Loading restaurant details…'}
        </p>
      </div>
    );
  }

  if (sessionChecked && !sessionId) {
    return (
      <div className="pt-24 flex flex-col w-full h-full items-center bg-[#FBFCFF]">
        <div className="w-full max-w-md mx-auto px-4 text-center">
          <h1 className="text-2xl font-bold text-[#171725] mb-4">Invalid Link</h1>
          <p className="text-[#696974]">
            We could not find a session in this link. Please reopen the feedback link shared
            via Tavahot.
          </p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="pt-24 flex flex-col w-full h-full items-center bg-[#FBFCFF]">
        <div className="w-full max-w-md mx-auto px-4 text-center">
          <FaStar className="text-6xl text-yellow-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-[#171725] mb-2">Thanks for the love!</h2>
          <p className="text-[#696974]">
            Your feedback helps {restaurantDetails?.name || 'this restaurant'} serve guests better.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-24 flex flex-col w-full h-full items-center bg-[#FBFCFF]">
      <div className="w-full max-w-md mx-auto px-4">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-[#171725] mb-2">
            How was {restaurantDetails?.name || 'your visit'}?
          </h1>
          <p className="text-[#696974]">
            Share your experience to help the restaurant improve every day.
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">{error}</div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-[#171725] mb-3">
              Rate your experience *
            </label>
            <div className="flex justify-center space-x-2">
              {[...Array(5)].map((_, index) => {
                const value = index + 1;
                return (
                  <FaStar
                    key={value}
                    className={`text-3xl cursor-pointer transition-colors ${
                      (hover || rating) >= value ? 'text-yellow-400' : 'text-gray-300'
                    }`}
                    onClick={() => setRating(value)}
                    onMouseEnter={() => setHover(value)}
                    onMouseLeave={() => setHover(null)}
                  />
                );
              })}
            </div>
            <div className="text-center mt-2 text-sm text-[#696974]">
              {rating === 0 && 'Select a rating'}
              {rating === 1 && 'Very Poor'}
              {rating === 2 && 'Could be better'}
              {rating === 3 && 'It was ok'}
              {rating === 4 && 'Pretty good'}
              {rating === 5 && 'Loved it'}
            </div>
          </div>

          {rating === 5 && submittingRating && (
            <div className="text-center mt-2">
              <span className="text-sm text-[#696974]">Submitting your rating...</span>
            </div>
          )}

          {rating === 5 && ratingSubmitted && (
            <div className="text-center p-4 bg-green-50 border border-green-200 rounded-md">
              <p className="text-green-800 font-medium">
                Glad! We could please you with our service! 🎉
              </p>
            </div>
          )}

          {rating !== 5 && (
            <div>
              <label htmlFor="feedback" className="block text-sm font-medium text-[#171725] mb-2">
                Share more details (optional)
              </label>
              <textarea
                id="feedback"
                value={feedback}
                onChange={(event) => setFeedback(event.target.value)}
                placeholder="Tell us about the food, service, or ambiance…"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent resize-none"
                rows="4"
                maxLength="500"
              />
              <div className="text-right mt-1">
                <span className="text-xs text-[#696974]">{feedback.length}/500</span>
              </div>
            </div>
          )}

          <div className="mt-6">
            {rating === 5 && ratingSubmitted ? (
              <div className="space-y-4">
                <button
                  type="button"
                  onClick={handleGoogleMapsRating}
                  className="w-full py-3 px-4 bg-[#FBC02D] text-white rounded-md font-medium hover:bg-[#F9A825] transition-colors"
                >
                  Rate us on Google Maps
                </button>
                <button
                  type="button"
                  onClick={skipGoogleMapsRating}
                  className="w-full py-3 px-4 bg-gray-300 text-gray-700 rounded-md font-medium hover:bg-gray-400 transition-colors"
                >
                  Skip for now
                </button>
              </div>
            ) : (
              <button
                type="submit"
                disabled={submitting || submittingRating || rating === 0}
                className={`w-full py-3 px-4 rounded-md font-medium transition-colors ${
                  submitting || submittingRating || rating === 0
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-[#FBC02D] text-white hover:bg-[#F9A825]'
                }`}
              >
                {submitting || submittingRating ? (
                  <div className="flex items-center justify-center">
                    <TailSpin
                      height="20"
                      width="20"
                      color="#ffffff"
                      ariaLabel="loading"
                      radius="1"
                      visible
                    />
                    <span className="ml-2">Submitting…</span>
                  </div>
                ) : (
                  'Submit Feedback'
                )}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

export default RateRestaurant;

